import React, { useState, useEffect } from "react";
import { ArrowLeftIcon } from "~/assets/icons";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { notify } from "~/utils/helper";
import jobDetailsApi, { IMaterialOrderDetail } from "~/apis/jobDetails";

interface MaterialOrderDetailViewProps {
  jobId: string;
  materialOrderId: string;
  onBack: () => void;
  onEdit?: (orderId: string) => void;
}

const MaterialOrderDetailView: React.FC<MaterialOrderDetailViewProps> = ({
  jobId,
  materialOrderId,
  onBack,
  onEdit,
}) => {
  const [orderDetail, setOrderDetail] = useState<IMaterialOrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Format date to readable format
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      // Use UTC methods to avoid timezone shifting
      const year = date.getUTCFullYear();
      const month = date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
      const day = date.getUTCDate();
      return `${month} ${day}, ${year}`;
    } catch {
      return dateString;
    }
  };

  // Format time to readable format
  const formatTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  // Format currency
  const formatCurrency = (amount: number | string): string => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  // Calculate total cost from sections
  const calculateTotalCost = (): number => {
    if (!orderDetail?.sections) return 0;
    return orderDetail.sections.reduce((total, section) => {
      return (
        total +
        section.lines.reduce((sectionTotal, line) => sectionTotal + line.lineTotal, 0)
      );
    }, 0);
  };

  // Calculate total items
  const calculateTotalItems = (): number => {
    if (!orderDetail?.sections) return 0;
    return orderDetail.sections.reduce((total, section) => total + section.lines.length, 0);
  };

  // Calculate total quantity
  const calculateTotalQuantity = (): number => {
    if (!orderDetail?.sections) return 0;
    return orderDetail.sections.reduce((total, section) => {
      return (
        total +
        section.lines.reduce(
          (sectionTotal, line) => sectionTotal + parseFloat(line.quantity || "0"),
          0
        )
      );
    }, 0);
  };

  // Format requested delivery date
  const formatRequestedDeliveryDate = (dateString: string): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      // Use UTC methods to avoid timezone shifting
      const year = date.getUTCFullYear();
      const month = date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
      const day = date.getUTCDate();
      return `${month} ${day}, ${year}`;
    } catch {
      return dateString;
    }
  };

  // Load material order detail
  useEffect(() => {
    const loadOrderDetail = async () => {
      try {
        setIsLoading(true);
        const response = await jobDetailsApi.getMaterialOrderDetail(jobId, materialOrderId);
        if (response.data.status === "success" && response.data.data) {
          setOrderDetail(response.data.data);
        } else {
          notify("error", response.data.message || "Failed to load order details");
        }
      } catch (error: any) {
        notify("error", error?.response?.data?.message || "Failed to load order details");
      } finally {
        setIsLoading(false);
      }
    };

    if (jobId && materialOrderId) {
      loadOrderDetail();
    }
  }, [jobId, materialOrderId]);

  const handleEdit = () => {
    if (onEdit) {
      onEdit(materialOrderId);
    }
  };

  const handleDownloadPDF = () => {
    // TODO: Implement download PDF functionality
    notify("info", "Download PDF functionality coming soon");
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#071427] text-white">
        <p className="text-[#A2A7B4]">Loading order details...</p>
      </div>
    );
  }

  if (!orderDetail) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#071427] text-white">
        <p className="text-[#A2A7B4]">Order not found</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-[#071427] text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#111e33]">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-[#1f2937] rounded transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-white" />
          </button>
          <h2 className="text-xl font-semibold text-white">Material Order Details</h2>
        </div>
        <div className="flex items-center gap-3">
          {orderDetail.status === "Draft" && (
            <PrimaryButton
              title="Edit Order"
              onClick={handleEdit}
              className="!bg-[#3b82f6] !text-white hover:!bg-[#2563eb] !rounded-lg !px-6"
            />
          )}
          {/* <PrimaryButton
            title="Download PDF"
            onClick={handleDownloadPDF}
            className="!bg-[#0C192D] !text-white !border !border-[#1f2937] hover:!bg-[#1f2937] !rounded-lg !px-6"
          /> */}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* General Information */}
          <div className="mb-8">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <span className="text-sm text-[#A2A7B4]">Branch:</span>
                <p className="text-base text-white mt-1">
                  {orderDetail.branchNumber ? `Branch ${orderDetail.branchNumber}` : "-"}
                </p>
              </div>
              <div>
                <span className="text-sm text-[#A2A7B4]">Material Order ID:</span>
                <p className="text-base text-white mt-1">{orderDetail.materialOrderId}</p>
              </div>
              <div>
                <span className="text-sm text-[#A2A7B4]">Order Date:</span>
                <p className="text-base text-white mt-1">{formatDate(orderDetail.date)}</p>
              </div>
              <div>
                <span className="text-sm text-[#A2A7B4]">Order Time:</span>
                <p className="text-base text-white mt-1">{formatTime(orderDetail.date)}</p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {orderDetail.sections && orderDetail.sections.length > 0 ? (
              orderDetail.sections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Section {sectionIndex + 1}
                  </h3>

                  {/* Table */}
                  <div className="bg-[#0E264C] rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-[#111E33] border-b border-[#111e33]">
                      <div
                        className="grid gap-4 px-4 py-3"
                        style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr 1fr" }}
                      >
                        <div className="text-sm font-semibold text-white">Item Name</div>
                        <div className="text-sm font-semibold text-white">UoM</div>
                        <div className="text-sm font-semibold text-white">Qty</div>
                        <div className="text-sm font-semibold text-white">Price</div>
                        <div className="text-sm font-semibold text-white">Line Total</div>
                        <div className="text-sm font-semibold text-white">Action</div>
                      </div>
                    </div>

                    {/* Table Body */}
                    <div className="bg-[#030E1F]">
                      {section.lines.map((line, lineIndex) => (
                        <div
                          key={lineIndex}
                          className={`grid gap-4 px-4 py-3 ${lineIndex !== section.lines.length - 1
                            ? "border-b border-[#111e33]"
                            : ""
                            }`}
                          style={{ gridTemplateColumns: "3fr 1fr 1fr 1fr 1fr 1fr" }}
                        >
                          {/* Item Name */}
                          <div className="text-sm text-white">
                            {line.product.itemDescription || line.product.itemNumber}
                          </div>

                          {/* UoM */}
                          <div className="text-sm text-white">{line.product.uom}</div>

                          {/* Qty */}
                          <div className="text-sm text-white">{line.quantity}</div>

                          {/* Price */}
                          <div className="text-sm text-white">{formatCurrency(line.price)}</div>

                          {/* Line Total */}
                          <div className="text-sm text-white">
                            {formatCurrency(line.lineTotal)}
                          </div>

                          {/* Action */}
                          <div className="text-sm text-red-500">Remove</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-[#A2A7B4] py-8">No items found</div>
            )}
          </div>

          {/* Delivery Address */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-6">Delivery Address</h3>
            <div className="space-y-4">
              <div>
                <span className="text-sm text-[#A2A7B4]">Street Address:</span>
                <p className="text-base text-white mt-1">
                  {orderDetail.streetAddress || "-"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <span className="text-sm text-[#A2A7B4]">City:</span>
                  <p className="text-base text-white mt-1">{orderDetail.city || "-"}</p>
                </div>
                <div>
                  <span className="text-sm text-[#A2A7B4]">State:</span>
                  <p className="text-base text-white mt-1">{orderDetail.state || "-"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <span className="text-sm text-[#A2A7B4]">ZIP Code:</span>
                  <p className="text-base text-white mt-1">{orderDetail.zipCode || "-"}</p>
                </div>
                <div>
                  <span className="text-sm text-[#A2A7B4]">Requested Delivery Date:</span>
                  <p className="text-base text-white mt-1">
                    {formatRequestedDeliveryDate(orderDetail.requestedDeliveryDate)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-6">Notes</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <span className="text-sm text-[#A2A7B4]">Notes:</span>
                <p className="text-base text-white mt-2 whitespace-pre-wrap">
                  {orderDetail.notes || orderDetail.internalNotes || "-"}
                </p>
              </div>
              <div>
                <span className="text-sm text-[#A2A7B4]">Notes to Supplier:</span>
                <p className="text-base text-white mt-2 whitespace-pre-wrap">
                  {orderDetail.notesToSupplier || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-8">
            <div className="bg-[#030E1F] rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>

              <div className="space-y-3">
                {/* Total Items */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">Total Items:</span>
                  <span className="text-sm text-white font-medium">
                    {calculateTotalItems()}
                  </span>
                </div>

                {/* Total Quantity */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">Total Quantity:</span>
                  <span className="text-sm text-white font-medium">
                    {calculateTotalQuantity()}
                  </span>
                </div>

                {/* Divider */}
                <div className="border-t border-[#3b82f6] my-3"></div>

                {/* Total Cost */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white">Total Cost:</span>
                  <span className="text-lg font-bold text-[#3b82f6]">
                    {formatCurrency(calculateTotalCost())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialOrderDetailView;

